export class DeviceDetector {
    constructor() {
        this.isMobile = this.detectMobile();
        this.isTablet = this.detectTablet();
        this.isLowPerformance = this.detectLowPerformance();
        this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        this.screenSize = this.detectScreenSize();
        this.orientation = this.detectOrientation();

        this.capabilities = this.getCapabilities();
        this.setupOrientationListener();
    }

    detectScreenSize() {
        const width = window.innerWidth;

        if (width <= 480) return 'small-mobile';
        if (width <= 768) return 'mobile';
        if (width <= 1024) return 'tablet';
        if (width <= 1440) return 'desktop';
        return 'large-desktop';
    }

    detectOrientation() {
        return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
    }

    setupOrientationListener() {
        window.addEventListener('resize', () => {
            this.screenSize = this.detectScreenSize();
            this.orientation = this.detectOrientation();
        });
    }

    isSmallMobile() {
        return this.screenSize === 'small-mobile';
    }

    isMobileSize() {
        return this.screenSize === 'small-mobile' || this.screenSize === 'mobile';
    }

    isTabletSize() {
        return this.screenSize === 'tablet';
    }

    isDesktopSize() {
        return this.screenSize === 'desktop' || this.screenSize === 'large-desktop';
    }

    isPortrait() {
        return this.orientation === 'portrait';
    }

    isLandscape() {
        return this.orientation === 'landscape';
    }

    detectMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    detectTablet() {
        return /iPad|Android(?!.*Mobile)/i.test(navigator.userAgent) || 
               (this.isMobile && window.screen.width >= 768);
    }

    detectLowPerformance() {
        if ('deviceMemory' in navigator) {
            return navigator.deviceMemory < 4;
        }
        
        if ('hardwareConcurrency' in navigator) {
            return navigator.hardwareConcurrency < 4;
        }
        
        return this.isMobile && !this.isTablet;
    }

    getCapabilities() {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        const capabilities = {
            webGL: !!gl,
            maxTextureSize: 1024,
            shadows: !this.isLowPerformance,
            antialiasing: !this.isMobile,
            particles: !this.isLowPerformance,
            reflections: !this.isMobile,
            pixelRatio: this.isLowPerformance ? 1 : Math.min(window.devicePixelRatio || 1, 2)
        };

        if (gl) {
            capabilities.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
            
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (debugInfo) {
                const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
                capabilities.renderer = renderer;
                
                if (renderer.includes('Intel') || renderer.includes('Mali') || renderer.includes('Adreno')) {
                    capabilities.shadows = false;
                    capabilities.particles = false;
                }
            }
        }

        return capabilities;
    }

    getQualitySettings() {
        // Mobile-optimized settings for smooth performance
        if (this.isLowPerformance || this.isSmallMobile()) {
            return {
                shadowMapSize: 512,
                maxLights: 2,
                particleCount: 15,
                renderScale: 0.75,
                antialiasing: false,
                postProcessing: false,
                pixelRatio: 1
            };
        } else if (this.isMobile || this.isMobileSize()) {
            return {
                shadowMapSize: this.isLowPerformance ? 512 : 1024,
                maxLights: 3,
                particleCount: 20,
                renderScale: this.isLowPerformance ? 0.75 : 1.0,
                antialiasing: !this.isLowPerformance,
                postProcessing: false,
                pixelRatio: Math.min(window.devicePixelRatio || 1, this.isLowPerformance ? 1.5 : 2)
            };
        } else if (this.isTabletSize()) {
            return {
                shadowMapSize: 1024,
                maxLights: 4,
                particleCount: 30,
                renderScale: 0.9,
                antialiasing: false,
                postProcessing: false,
                pixelRatio: Math.min(window.devicePixelRatio || 1, 2)
            };
        } else {
            return {
                shadowMapSize: 2048,
                maxLights: 8,
                particleCount: 50,
                renderScale: 1,
                antialiasing: true,
                postProcessing: true,
                pixelRatio: Math.min(window.devicePixelRatio || 1, 2)
            };
        }
    }

    getResponsiveFOV() {
        // Wider FOV on mobile for better scene visibility
        if (this.isSmallMobile()) return 115;
        if (this.isMobileSize()) return 110;
        if (this.isTabletSize()) return 100;
        return 90;
    }

    getResponsiveScaleFactor() {
        // Increased scale factors for better mobile visibility
        // User feedback: objects were too small in zoomed-out view
        if (this.isSmallMobile()) return 0.85;  // was 0.5 (+70% increase)
        if (this.isMobileSize()) return 0.95;   // was 0.65 (+46% increase)
        if (this.isTabletSize()) return 1.0;    // was 0.8 (+25% increase)
        return 1.0;
    }

    getCameraDistanceMultiplier() {
        // Adjust camera distance based on screen size
        if (this.isSmallMobile()) return 1.3;
        if (this.isMobileSize()) return 1.2;
        if (this.isTabletSize()) return 1.1;
        return 1.0;
    }

    optimizeForDevice(renderer, scene) {
        const quality = this.getQualitySettings();
        
        renderer.setPixelRatio(quality.renderScale * this.pixelRatio);
        renderer.shadowMap.enabled = quality.shadowMapSize > 0;
        
        if (renderer.shadowMap.enabled) {
            renderer.shadowMap.mapSize.setScalar(quality.shadowMapSize);
        }
        
        if (!this.capabilities.antialiasing) {
            renderer.antialias = false;
        }
        
        scene.traverse((object) => {
            if (object.isLight && object.shadow) {
                if (quality.shadowMapSize === 0) {
                    object.castShadow = false;
                } else {
                    object.shadow.mapSize.setScalar(quality.shadowMapSize);
                }
            }
        });
    }

    logDeviceInfo() {
        console.log('Device Info:', {
            isMobile: this.isMobile,
            isTablet: this.isTablet,
            isLowPerformance: this.isLowPerformance,
            pixelRatio: this.pixelRatio,
            capabilities: this.capabilities,
            qualitySettings: this.getQualitySettings()
        });
    }
}